'use client';

import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { useIsClient } from 'usehooks-ts';
import { v4 } from 'uuid';
import { DOTS, usePagination } from '../../reactWrapper/hooks/use-pagination';
import { cn } from '../ui-x';

/**
 * The reference has no pagination (it runs on fixtures), so this keeps the
 * existing behaviour and only adopts the grammar: navy active control, surface
 * idle, tabular numerals.
 */
/**
 * Props are declared so TypeScript call sites see them as OPTIONAL; an
 * untyped .jsx component has all of its params inferred as required.
 *
 * @param {{
 *   rangeStart?: number,
 *   totalCount?: number,
 *   currentPage?: number,
 *   siblingCount?: number,
 *   pageSize?: number,
 *   pageColumn?: number,
 *   className?: string,
 *   onPageChange?: (page: number) => void,
 * }} props
 */
const Pagination = ({
  rangeStart,
  totalCount,
  currentPage,
  siblingCount,
  pageSize = 10,
  pageColumn = 1,
  className,
  onPageChange,
}) => {
  const isClient = useIsClient();
  const paginationRange = usePagination({
    currentPage,
    totalCount: totalCount / pageColumn,
    siblingCount,
    pageSize,
  });

  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange && onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange && onPageChange(currentPage - 1);
  };

  let lastPage = paginationRange[paginationRange.length - 1];

  if (!isClient) return null;

  const arrow =
    'grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border bg-card text-brand-navy transition-colors hover:bg-surface disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className={className}>
      <nav
        aria-label="Pagination"
        className="flex items-center justify-center gap-1.5 pb-4 lg:pb-0"
      >
        <button
          type="button"
          className={arrow}
          disabled={currentPage === 1}
          onClick={onPrevious}
          aria-label="Previous page"
        >
          <MdOutlineKeyboardArrowLeft className="h-5 w-5" />
        </button>

        {paginationRange &&
          paginationRange.map((pageNumber) => {
            if (pageNumber === DOTS) {
              return (
                <span
                  key={v4()}
                  className="grid h-9 w-9 place-items-center text-sm text-muted-foreground"
                >
                  &#8230;
                </span>
              );
            }

            const active = pageNumber === currentPage;

            return (
              <button
                type="button"
                key={pageNumber}
                onClick={() => onPageChange && onPageChange(Number(pageNumber))}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'grid h-9 min-w-9 place-items-center rounded-lg px-2 text-sm font-semibold tabular-nums transition-colors',
                  active
                    ? 'bg-brand-navy text-white'
                    : 'border border-border bg-card text-brand-navy hover:bg-surface'
                )}
              >
                {pageNumber}
              </button>
            );
          })}

        <button
          type="button"
          className={arrow}
          disabled={currentPage === lastPage}
          onClick={onNext}
          aria-label="Next page"
        >
          <MdOutlineKeyboardArrowRight className="h-5 w-5" />
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
